import { useEffect, useRef } from "react";
import Swal from "sweetalert2";

import LeadService from "services/lead.service";

const PROCESSING_INTERVAL = 3000; // cartão: confirmação chega em segundos
const PROCESSING_MAX_ATTEMPTS = 10; // ~30 s
const PIX_INTERVAL = 10000; // Pix: o DJ ainda precisa pagar
const PIX_MAX_ATTEMPTS = 12; // ~2 min

const clearUrl = () => window.history.replaceState({}, document.title, window.location.pathname);

// Trata a volta do Stripe na liberação de pedido:
// /dashboard/pedidos?unlock=success&session_id=cs_... | ?unlock=cancel
// A confirmação vem sempre da API (unlock-status), nunca só da URL.
export default function useUnlockReturn({ onConfirmed, onRetry }) {
  // Callbacks por ref: o efeito roda uma vez só, mas chama sempre a versão atual
  const onConfirmedRef = useRef(onConfirmed);
  const onRetryRef = useRef(onRetry);
  onConfirmedRef.current = onConfirmed;
  onRetryRef.current = onRetry;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("unlock");
    const sessionId = params.get("session_id");
    if (!result) return undefined;

    if (result === "cancel") {
      clearUrl();
      Swal.fire({
        icon: "info",
        title: "Pagamento cancelado",
        text: "O pedido continua disponível para liberar quando você quiser.",
      });
      return undefined;
    }

    if (result !== "success" || !sessionId) {
      clearUrl();
      return undefined;
    }

    let cancelled = false;
    let timer;
    let attempts = 0;
    let pixNoticeShown = false;

    const check = async () => {
      attempts += 1;
      let res = null;
      try {
        res = await LeadService.getUnlockStatus(sessionId);
      } catch (e) {
        res = null; // falha de rede: conta como tentativa e tenta de novo
      }
      if (cancelled) return;

      const status = res?.status;

      if (status === "confirmed") {
        clearUrl();
        if (onConfirmedRef.current) await onConfirmedRef.current(res.lead);
        Swal.fire({
          icon: "success",
          title: "Contato liberado!",
          text: "O pedido está em Meus pedidos com os dados do cliente.",
        });
        return;
      }

      if (status === "failed") {
        clearUrl();
        const answer = await Swal.fire({
          icon: "error",
          title: "Pagamento não concluído",
          text: "O pagamento não foi aprovado. Você pode tentar liberar o pedido de novo.",
          showCancelButton: true,
          confirmButtonText: "Tentar de novo",
          cancelButtonText: "Fechar",
        });
        if (answer.isConfirmed && onRetryRef.current) onRetryRef.current();
        return;
      }

      if (status === "pix_pending") {
        if (!pixNoticeShown) {
          pixNoticeShown = true;
          Swal.fire({
            icon: "info",
            title: "Aguardando o Pix",
            text: "Pague o Pix para liberar. O contato aparece assim que o pagamento confirmar.",
          });
        }
        if (attempts < PIX_MAX_ATTEMPTS) {
          timer = setTimeout(check, PIX_INTERVAL);
        } else {
          // O card mostra "Pagamento em andamento" (unlock_pending) até confirmar
          clearUrl();
        }
        return;
      }

      // processing (pago, confirmação a caminho) ou erro na consulta
      if (attempts < PROCESSING_MAX_ATTEMPTS) {
        timer = setTimeout(check, PROCESSING_INTERVAL);
        return;
      }

      // Mantém a URL: recarregar a página consulta de novo
      Swal.fire({
        icon: "info",
        title: "Confirmando pagamento",
        text: "Seu pagamento está sendo processado. Atualize a página em alguns instantes.",
      });
    };

    check();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);
}
